import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Package, Search, Clock, CheckCircle, XCircle,
  Eye, ThumbsUp, MapPin, ExternalLink, ShoppingCart,
  User, Phone, Mail, DollarSign, Truck, X, Check,
  MessageCircle, Send
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const LinkAmazonAdmin = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const token = localStorage.getItem("enterprise_token");
  const user = JSON.parse(localStorage.getItem("enterprise_user") || "{}");

  useEffect(() => {
    if (!token || user.role !== "super_admin") {
      navigate("/enterprise/login");
      return;
    }
    fetchOrders();
  }, [token, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/linkamazon/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Erè nan chaje kòmand yo");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/linkamazon/admin/orders/${orderId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Kòmand apwouve!");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan apwouve kòmand");
    }
  };

  const handleConfirmPayment = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/linkamazon/admin/orders/${orderId}/confirm-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Peman konfime!");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan konfime peman");
    }
  };

  const handleMarkComplete = async (orderId) => {
    const amazonOrderId = prompt("Antre Amazon Order ID (opsyonèl):");
    try {
      const response = await axios.post(
        `${API}/linkamazon/admin/orders/${orderId}/complete`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { amazon_order_id: amazonOrderId || "" }
        }
      );
      if (response.data.success) {
        toast.success("Kòmand make konplete!");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan make kòmand konplete");
    }
  };

  const openAmazonPurchase = (order) => {
    // Create Amazon search URL for each item
    const items = order.items || [];
    if (items.length > 0) {
      // Open Amazon with first item search
      const searchQuery = encodeURIComponent(items[0].name);
      window.open(`https://www.amazon.com/s?k=${searchQuery}`, '_blank');
    } else {
      window.open('https://www.amazon.com', '_blank');
    }
  };

  const sendWhatsAppReminder = (order, type) => {
    const buyerName = order.buyer_name || 'Kliyan';
    const phone = order.buyer_phone;

    if (!phone) {
      toast.error(`${buyerName} pa gen nimewo telef\u00f2n!`);
      return;
    }

    let message = '';
    switch (type) {
      case 'payment':
        message = `Bonjou ${buyerName}!\n\nSa a se yon rap\u00e8l pou peman k\u00f2mand #${order.order_number}.\n\nMontan: $${(order.subtotal || 0).toFixed(2)} USD (${(order.total_htg || 0).toLocaleString()} HTG)\n\nTanpri f\u00e8 peman an pi vit posib pou nou ka pase k\u00f2mand la sou Amazon pou ou.\n\nM\u00e8si!\n- BVGLINKAMAZON`;
        break;
      case 'approved':
        message = `Bonjou ${buyerName}!\n\nBon nouv\u00e8l! K\u00f2mand ou #${order.order_number} apwouve!\n\nMontan: $${(order.subtotal || 0).toFixed(2)} USD\n\nTanpri f\u00e8 peman pou nou ka komansey achte pou ou sou Amazon.\n\nM\u00e8si!\n- BVGLINKAMAZON`;
        break;
      case 'completed':
        message = `Bonjou ${buyerName}!\n\nK\u00f2mand ou #${order.order_number} konpl\u00e8! Nou pase li sou Amazon.\n\n${order.amazon_order_id ? `Amazon Order ID: ${order.amazon_order_id}\n` : ''}Ou ka swiv li nan kont ou sou BVGLINKAMAZON.\n\nM\u00e8si!\n- BVGLINKAMAZON`;
        break;
      case 'tracking':
        message = `Bonjou ${buyerName}!\n\nK\u00f2mand #${order.order_number} an wout!\n\n${order.tracking_number ? `Nimewo swiv: ${order.tracking_number}\n` : ''}Tanpri konekte sou BVGLINKAMAZON pou w\u00e8 detay livrezon an.\n\nM\u00e8si!\n- BVGLINKAMAZON`;
        break;
      default:
        message = `Bonjou ${buyerName}!\n\nNou kontakte ou konsènan k\u00f2mand #${order.order_number}.\n\nMontan: $${(order.subtotal || 0).toFixed(2)} USD\n\nTanpri konekte sou BVGLINKAMAZON pou plis detay.\n\nM\u00e8si!\n- BVGLINKAMAZON`;
    }

    // Clean phone number
    let cleanPhone = phone.replace(/[^\d+]/g, '');
    if (cleanPhone.startsWith('+')) cleanPhone = cleanPhone.substring(1);
    if (cleanPhone.length === 8) cleanPhone = '509' + cleanPhone;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');
    toast.success(`WhatsApp ouvri pou ${buyerName}`);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending_approval: { bg: "bg-yellow-100 text-yellow-800", label: "An Atant Apwobasyon", icon: Clock },
      approved: { bg: "bg-blue-100 text-blue-800", label: "Apwouve - Ap Tann Peman", icon: ThumbsUp },
      pending_payment: { bg: "bg-orange-100 text-orange-800", label: "Ap Tann Peman", icon: DollarSign },
      pending_payment_verification: { bg: "bg-purple-100 text-purple-800", label: "Ap Verifye Peman", icon: Search },
      paid: { bg: "bg-green-100 text-green-800", label: "Peye - Pase sou Amazon", icon: ShoppingCart },
      completed: { bg: "bg-emerald-100 text-emerald-800", label: "Konplè", icon: CheckCircle },
      rejected: { bg: "bg-red-100 text-red-800", label: "Rejte", icon: XCircle }
    };
    const c = config[status] || { bg: "bg-gray-100 text-gray-800", label: status, icon: Package };
    const Icon = c.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1 ${c.bg}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: orders.length,
    pending_approval: orders.filter(o => o.status === "pending_approval").length,
    approved: orders.filter(o => o.status === "approved").length,
    pending_payment: orders.filter(o => o.status === "pending_payment").length,
    pending_payment_verification: orders.filter(o => o.status === "pending_payment_verification").length,
    paid: orders.filter(o => o.status === "paid").length,
    completed: orders.filter(o => o.status === "completed").length
  };

  if (loading) {
    return (
      <EnterpriseLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9900]"></div>
        </div>
      </EnterpriseLayout>
    );
  }

  return (
    <EnterpriseLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#232F3E]">Kòmand LinkAmazon</h1>
            <p className="text-gray-500">Jere kòmand kliyan yo</p>
          </div>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-8 h-8 text-[#FF9900]" />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { key: "all", label: "Tout", color: "bg-gray-100" },
            { key: "pending_approval", label: "An Atant", color: "bg-yellow-100" },
            { key: "approved", label: "Apwouve", color: "bg-blue-100" },
            { key: "pending_payment", label: "Tann Peman", color: "bg-orange-100" },
            { key: "pending_payment_verification", label: "Verifye Peman", color: "bg-purple-100" },
            { key: "paid", label: "Peye", color: "bg-green-100" },
            { key: "completed", label: "Konplè", color: "bg-emerald-100" }
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`p-3 rounded-lg text-center transition-all ${statusFilter === key ? "ring-2 ring-[#FF9900]" : ""
                } ${color}`}
            >
              <p className="text-2xl font-bold">{statusCounts[key]}</p>
              <p className="text-xs text-gray-600">{label}</p>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Chèche pa nimewo kòmand, non, oswa imèl..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Pa gen kòmand</p>
              </CardContent>
            </Card>
          ) : (
            filteredOrders.map((order) => (
              <Card
                key={order.id}
                className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedOrder(order)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <p className="font-bold text-lg text-[#232F3E]">{order.order_number}</p>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {order.buyer_name || "N/A"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {order.buyer_email || "N/A"}
                        </span>
                        {order.shipping_service === "DEKO" && (
                          <span className="flex items-center gap-1 text-red-600 font-medium">
                            <Truck className="w-4 h-4" />
                            DEKO Shipping
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">
                        {order.items?.length || 0} atik • {new Date(order.created_at).toLocaleString('ht-HT')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#FF9900]">${(order.subtotal || 0).toFixed(2)}</p>
                      <p className="text-sm text-gray-500">{(order.total_htg || 0).toLocaleString()} HTG</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-start justify-between sticky top-0 bg-white z-10 border-b">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {selectedOrder.order_number}
                </CardTitle>
                <div className="mt-2">{getStatusBadge(selectedOrder.status)}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              {/* Customer Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-800">
                  <User className="w-4 h-4" />
                  Enfòmasyon Kliyan
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Non:</p>
                    <p className="font-medium">{selectedOrder.buyer_name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Imèl:</p>
                    <p className="font-medium">{selectedOrder.buyer_email || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Telefòn:</p>
                    <p className="font-medium">{selectedOrder.buyer_phone || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Dat:</p>
                    <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString('ht-HT')}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  {selectedOrder.buyer_phone && (
                    <>
                      <button
                        onClick={() => sendWhatsAppReminder(selectedOrder, 'payment')}
                        className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                        title="Voye rapèl peman via WhatsApp"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        Rapèl Peman
                      </button>
                      <button
                        onClick={() => sendWhatsAppReminder(selectedOrder, selectedOrder.status === 'completed' ? 'completed' : 'approved')}
                        className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                        title="Voye mesaj via WhatsApp"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {selectedOrder.status === 'completed' ? 'Notifye Konplè' : 'Mesaj Kliyan'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#FF9900]" />
                  Atik yo ({selectedOrder.items?.length || 0})
                </h3>
                <div className="border rounded-lg divide-y">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">Kantite: {item.quantity} × ${item.unit_price?.toFixed(2)}</p>
                      </div>
                      <p className="font-bold">${(item.total || item.quantity * item.unit_price).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Detay Pri</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Sou-Total Amazon:</span>
                    <span>${(selectedOrder.amazon_subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#FF9900]">
                    <span>Frè BVGLINKAMAZON (10%):</span>
                    <span>+${(selectedOrder.bvgpromo_fee || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total USD:</span>
                    <span>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#FF9900] font-bold">
                    <span>Total HTG:</span>
                    <span>{(selectedOrder.total_htg || 0).toLocaleString()} HTG</span>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div className={`p-4 rounded-lg ${selectedOrder.shipping_service === "DEKO" ? "bg-gradient-to-r from-blue-50 to-red-50 border border-red-200" : "bg-green-50"}`}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Adrès Livrezon
                    {selectedOrder.shipping_service === "DEKO" && (
                      <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">DEKO SHIPPING</span>
                    )}
                  </h3>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">{selectedOrder.shipping_address.name}</p>
                    <p>{selectedOrder.shipping_address.street}</p>
                    <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zip}</p>
                    <p>{selectedOrder.shipping_address.country}</p>
                    {selectedOrder.shipping_address.phone && (
                      <p className="flex items-center gap-1 mt-2 text-gray-600">
                        <Phone className="w-3 h-3" />
                        {selectedOrder.shipping_address.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Info */}
              {selectedOrder.payment?.method && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Enfòmasyon Peman</h3>
                  <p className="text-sm">
                    <span className="text-gray-500">Metòd:</span>{" "}
                    <span className="font-medium uppercase">{selectedOrder.payment.method}</span>
                  </p>
                  {selectedOrder.payment.status && (
                    <p className="text-sm">
                      <span className="text-gray-500">Status:</span>{" "}
                      <span className="font-medium">{selectedOrder.payment.status}</span>
                    </p>
                  )}
                  {selectedOrder.payment.proof_image && (
                    <div className="mt-4 border-t border-purple-200 pt-3">
                      <p className="text-[10px] font-bold uppercase text-purple-400 mb-2 tracking-wider">Prèv Peman (Foto/Screenshot):</p>
                      <div className="relative group overflow-hidden rounded-lg border-2 border-purple-200 shadow-inner bg-white">
                        <img
                          src={selectedOrder.payment.proof_image}
                          alt="Proof"
                          className="w-full h-auto max-h-[400px] object-contain transition-transform duration-300 group-hover:scale-[1.02] cursor-zoom-in"
                          onClick={() => window.open(selectedOrder.payment.proof_image, '_blank')}
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-black/50 p-2 text-white text-[10px] text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          Klike sou foto a pou wè l pi byen
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Nòt Kliyan</h3>
                  <p className="text-sm">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t">
                {/* Pending Approval - Show Approve Button */}
                {selectedOrder.status === "pending_approval" && (
                  <Button
                    onClick={() => handleApprove(selectedOrder.id)}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg font-bold"
                  >
                    <ThumbsUp className="w-5 h-5 mr-2" />
                    Apwouve Kòmand
                  </Button>
                )}

                {/* Pending Payment - Show Confirm Payment Button */}
                {(selectedOrder.status === "approved" || selectedOrder.status === "pending_payment" || selectedOrder.status === "pending_payment_verification") && (
                  <Button
                    onClick={() => handleConfirmPayment(selectedOrder.id)}
                    className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg font-bold"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Konfime Peman Resevwa
                  </Button>
                )}

                {/* Paid - Show Amazon Button and Complete Button */}
                {selectedOrder.status === "paid" && (
                  <>
                    <Button
                      onClick={() => openAmazonPurchase(selectedOrder)}
                      className="w-full bg-[#FF9900] hover:bg-[#E88B00] py-6 text-lg font-bold"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Ale sou Amazon Pase Kòmand
                    </Button>
                    <Button
                      onClick={() => handleMarkComplete(selectedOrder.id)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg font-bold"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Make Kòmand Konplete
                    </Button>
                  </>
                )}

                {/* Completed Status */}
                {selectedOrder.status === "completed" && (
                  <div className="bg-emerald-50 p-4 rounded-lg flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                    <div>
                      <p className="font-bold text-emerald-800">Kòmand Konplete!</p>
                      {selectedOrder.amazon_order_id && (
                        <p className="text-sm text-emerald-600">Amazon Order ID: {selectedOrder.amazon_order_id}</p>
                      )}
                      {selectedOrder.completed_at && (
                        <p className="text-sm text-emerald-600">
                          Konplete: {new Date(selectedOrder.completed_at).toLocaleString('ht-HT')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </EnterpriseLayout>
  );
};

